import type { LinkedInPostData } from '../components/LinkedInPostsCard';

interface RawInstagramComment {
  text: string;
  ownerUsername: string;
  ownerFullName?: string;
  timestamp?: string;
  likesCount?: number;
}

interface RawInstagramPost {
  url?: string;
  caption?: string;
  type?: string;
  images?: string[];
  displayUrl?: string;
  likesCount?: number;
  commentsCount?: number;
  timestamp?: string;
  locationName?: string;
  hashtags?: string[];
  comments?: RawInstagramComment[];
}

interface InstagramPostsSource {
  fullName?: string | null;
  customFields?: Record<string, unknown> | null;
  contacts?: Array<{
    name?: string | null;
    isPrimary?: boolean;
    customFields?: Record<string, unknown> | null;
  }> | null;
}

function mapPost(post: RawInstagramPost, authorName: string): LinkedInPostData {
  return {
    text: [post.caption, post.hashtags?.map((h) => `#${h}`).join(' ')].filter(Boolean).join('\n\n'),
    url: post.url || '',
    type: post.type,
    images: post.images ?? (post.displayUrl ? [post.displayUrl] : undefined),
    numLikes: post.likesCount ?? 0,
    numComments: post.commentsCount ?? 0,
    postedAt: post.timestamp ?? '',
    authorName,
    comments: post.comments?.map((c) => ({
      text: c.text,
      authorName: c.ownerFullName || c.ownerUsername,
      authorHeadline: '',
      authorProfileUrl: `https://www.instagram.com/${c.ownerUsername}`,
    })),
  };
}

function postsFrom(customFields: Record<string, unknown> | null | undefined): RawInstagramPost[] {
  const posts = customFields?.instagramPosts;
  return Array.isArray(posts) ? (posts as RawInstagramPost[]) : [];
}

export function collectInstagramPosts(lead: InstagramPostsSource): LinkedInPostData[] {
  const leadPosts = postsFrom(lead.customFields).map((p) => mapPost(p, lead.fullName || ''));

  const contactPosts = (lead.contacts ?? []).flatMap((contact) =>
    postsFrom(contact.customFields).map((p) => mapPost(p, contact.name || lead.fullName || '')),
  );

  const seen = new Set<string>();
  const merged: LinkedInPostData[] = [];
  for (const post of [...leadPosts, ...contactPosts]) {
    const key = post.url || post.text;
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(post);
  }

  return merged.sort(
    (a, b) => new Date(b.postedAt || 0).getTime() - new Date(a.postedAt || 0).getTime(),
  );
}
